import React, { useState } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { useSiteData } from '../context/SiteDataContext';
import { Phone, Mail, MapPin, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const { language, t } = useLanguage();
  const { companyInfo } = useSiteData();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    
    try {
      const response = await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        message: formData.message,
        language: language
      });
      
      if (response.data.success) {
        setIsSubmitted(true);
        toast.success(language === 'sv' ? 'Meddelande skickat!' : 'Message sent!');
        
        // Reset form after 5 seconds
        setTimeout(() => {
          setIsSubmitted(false);
          setFormData({ name: '', email: '', phone: '', message: '' });
        }, 5000);
      }
    } catch (err) {
      console.error('Contact form error:', err);
      const errorMessage = language === 'sv' 
        ? 'Något gick fel. Försök igen eller kontakta oss direkt.'
        : 'Something went wrong. Please try again or contact us directly.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
    // Clear error when user starts typing
    if (error) setError(null);
  };

  return (
    <main>
      {/* Hero */}
      <section className="relative py-12 md:py-20 bg-gradient-to-br from-purple-900 via-purple-800 to-purple-900 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 right-20 w-72 h-72 bg-orange-400 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-20 w-96 h-96 bg-purple-400 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 md:mb-6">
            {t.contact.title}
          </h1>
          <p className="text-lg md:text-xl text-purple-100 max-w-2xl mx-auto">
            {t.contact.subtitle}
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-12 md:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16">
            {/* Contact Form */}
            <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 border border-gray-100">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6">
                {language === 'sv' ? 'Skicka ett meddelande' : 'Send a message'}
              </h2>
              
              {isSubmitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="text-green-600" size={32} />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {language === 'sv' ? 'Tack för ditt meddelande!' : 'Thank you for your message!'}
                  </h3>
                  <p className="text-gray-600">
                    {language === 'sv' 
                      ? 'Vi har skickat en bekräftelse till din e-post och återkommer så snart som möjligt.' 
                      : 'We have sent a confirmation to your email and will get back to you as soon as possible.'
                    }
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5 md:space-y-6">
                  {error && (
                    <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                      <AlertCircle size={20} />
                      <span className="text-sm">{error}</span>
                    </div>
                  )}
                  
                  <div>
                    <Label htmlFor="name" className="text-gray-700">
                      {t.contact.name} *
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      minLength={2}
                      className="mt-2"
                      placeholder={language === 'sv' ? 'För- och efternamn' : 'First and last name'}
                    />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="email" className="text-gray-700">
                        {t.contact.email} *
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="mt-2"
                        placeholder="din@email.se"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-gray-700">
                        {t.contact.phone}
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        className="mt-2"
                        placeholder="070-123 45 67"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="message" className="text-gray-700">
                      {t.contact.message} *
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      minLength={10}
                      className="mt-2 min-h-[150px]"
                      placeholder={language === 'sv' 
                        ? 'Beskriv vad du behöver hjälp med...'
                        : 'Describe what you need help with...'
                      }
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white py-5 md:py-6 text-base md:text-lg"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center gap-2">
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        {language === 'sv' ? 'Skickar...' : 'Sending...'}
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Send size={20} />
                        {t.contact.send}
                      </span>
                    )}
                  </Button>
                </form>
              )}
            </div>

            {/* Contact Info */}
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 md:mb-8">
                {t.footer.contactInfo}
              </h2>
              
              <div className="space-y-4 md:space-y-6 mb-8 md:mb-12">
                <a
                  href={`tel:${companyInfo.phone}`}
                  className="flex items-center gap-4 p-4 bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
                >
                  <div className="w-12 h-12 md:w-14 md:h-14 bg-purple-100 rounded-xl flex items-center justify-center">
                    <Phone className="text-purple-600" size={22} />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t.contact.callUs}</p>
                    <p className="text-base md:text-lg font-semibold text-gray-900">{companyInfo.phone}</p>
                  </div>
                </a>
                
                <a
                  href={`mailto:${companyInfo.email}`}
                  className="flex items-center gap-4 p-4 bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
                >
                  <div className="w-12 h-12 md:w-14 md:h-14 bg-orange-100 rounded-xl flex items-center justify-center">
                    <Mail className="text-orange-600" size={22} />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t.contact.emailUs}</p>
                    <p className="text-base md:text-lg font-semibold text-gray-900">{companyInfo.email}</p>
                  </div>
                </a>
                
                <div className="flex items-center gap-4 p-4 bg-white rounded-xl shadow-sm border border-gray-100">
                  <div className="w-12 h-12 md:w-14 md:h-14 bg-purple-100 rounded-xl flex items-center justify-center">
                    <MapPin className="text-purple-600" size={22} />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t.contact.address}</p>
                    <p className="text-base md:text-lg font-semibold text-gray-900">{companyInfo.address}</p>
                  </div>
                </div>
              </div>

              {/* Google Maps */}
              <div className="rounded-2xl overflow-hidden shadow-xl border border-gray-100">
                <iframe
                  title="Stockholm Elite Städ Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2034.5!2d18.07!3d59.34!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x465f9d6a5c5c5c5c%3A0x5c5c5c5c5c5c5c5c!2sStor%C3%A4ngsgatan%209%2C%20115%2044%20Stockholm!5e0!3m2!1ssv!2sse!4v1234567890"
                  width="100%"
                  height="250"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="md:h-[300px]"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ContactPage;
